import { FormEvent, Suspense, lazy, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { ArrowRight, Axis3d, BarChart3, BrainCircuit, CheckCircle2, Code2, Download, Glasses, Monitor, Quote, ScanEye, Smartphone, Sparkles, Star, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../lib/api';
import { SOLUTIONS } from '../../data/solutions';

const Hero3D = lazy(() => import('../../components/Hero3D'));
const EcosystemOrb3D = lazy(() => import('../../components/EcosystemOrb3D'));

function buildPreviewUrl(url: string): string {
  return `https://s.wordpress.com/mshots/v1/${encodeURIComponent(url)}?w=1200`;
}

const FEATURED_PROJECTS = [
  {
    id: 1,
    title: 'Tank Config Ziemann Holvrieka',
    category: 'Configurateur 2D/3D',
    client: 'Ziemann Holvrieka',
    url: 'https://tank-config.ziemann-holvrieka.com/',
    img: buildPreviewUrl('https://tank-config.ziemann-holvrieka.com/'),
    color: 'from-red-500 to-red-700',
    description: 'Configurateur industriel 2D/3D pour accélérer la qualification commerciale et réduire les allers-retours techniques.',
    tags: ['3D temps réel', 'Produit complexe', 'Web app'],
  },
  {
    id: 2,
    title: 'American Logo Watch',
    category: 'E-commerce',
    client: 'AmericanLogoWatch',
    url: 'https://americanlogowatch.com/',
    img: buildPreviewUrl('https://americanlogowatch.com/'),
    color: 'from-red-600 to-slate-950',
    description: 'Expérience e-commerce headless avec parcours produit fluide, performance front-end et intégrations métier.',
    tags: ['E-commerce', 'Headless', 'UX produit'],
  },
  // {
  //   id: 3,
  //   title: 'Gallery Print',
  //   category: 'E-commerce',
  //   client: 'GalleryPrint',
  //   url: 'https://galleryprint.be',
  //   img: buildPreviewUrl('https://galleryprint.be'),
  //   color: 'bg-red-100',
  // },
];

const TESTIMONIALS = [
  { name: 'Jean-Marc L.', role: 'CTO @ RetailCorp', text: 'Ahime a transformé notre infrastructure. Nous sommes passés de 6s à 1.5s de LCP en 3 mois.', img: 'https://picsum.photos/seed/user1/100/100' },
  { name: 'Sophie V.', role: 'Dir. Opérations @ BuildIt', text: 'Leur ERP métier est le seul qui a réellement compris les contraintes du terrain.', img: 'https://picsum.photos/seed/user2/100/100' },
];

const FUTURE_LAB_TRACKS = [
  {
    key: '3d',
    anchor: '3D',
    title: '3D Product Universes',
    description: 'Configurateurs, digital twins et experiences web interactives pour vendre plus vite des offres complexes.',
    metric: 'x2.4 engagement sur les parcours produits',
    chips: ['Digital Twin', 'Rendu temps reel', 'Parcours immersif'],
    icon: Axis3d,
    gradient: 'from-red-500/30 via-red-400/10 to-red-600/25',
    glow: 'bg-red-400/45',
    border: 'border-red-200/80',
    iconTone: 'text-red-700',
    iconShell: 'bg-red-500/15 border-red-200/80',
    progress: 'from-red-500 to-red-600',
    span: '',
  },
  {
    key: 'ai',
    anchor: 'IA',
    title: 'IA Operationnelle',
    description: 'Assistants IA, orchestration des workflows et moteurs de prediction relies a vos donnees metier.',
    metric: '-38% de temps sur les taches repetitives',
    chips: ['Agents internes', 'RAG securise', 'Automatisation'],
    icon: BrainCircuit,
    gradient: 'from-red-500/25 via-red-400/10 to-red-500/25',
    glow: 'bg-red-400/40',
    border: 'border-red-200/80',
    iconTone: 'text-red-700',
    iconShell: 'bg-red-500/15 border-red-200/80',
    progress: 'from-red-500 to-red-500',
    span: '',
  },
  {
    key: 'ar',
    anchor: 'AR',
    title: 'AR Field Intelligence',
    description: 'Superposition de donnees en temps reel sur le terrain pour le support, la maintenance et la formation.',
    metric: '-27% d erreurs sur interventions critiques',
    chips: ['Vision assistee', 'Guidage contextualise', 'Assistance distante'],
    icon: ScanEye,
    gradient: 'from-red-500/30 via-red-400/10 to-red-500/25',
    glow: 'bg-red-400/45',
    border: 'border-red-200/80',
    iconTone: 'text-red-700',
    iconShell: 'bg-red-500/15 border-red-200/80',
    progress: 'from-red-500 to-red-500',
    span: '',
  },
  {
    key: 'vr',
    anchor: 'VR',
    title: 'VR Training Labs',
    description: 'Simulations haute fidelite pour entrainer vos equipes dans des scenarios techniques a fort enjeu.',
    metric: '+62% de retention des protocoles',
    chips: ['Scenarios immersifs', 'Analytics de sessions', 'Onboarding accelere'],
    icon: Glasses,
    gradient: 'from-red-500/30 via-red-500/10 to-red-500/20',
    glow: 'bg-red-400/40',
    border: 'border-red-200/80',
    iconTone: 'text-red-700',
    iconShell: 'bg-red-500/15 border-red-200/80',
    progress: 'from-red-500 to-red-500',
    span: '',
  },
  {
    key: 'innovation',
    anchor: 'Innovation',
    title: 'Innovation Studio',
    description: 'De l intuition au prototype: nous transformons les idees ambitieuses en produits testables en quelques semaines.',
    metric: 'MVP industrialisable en 6 semaines',
    chips: ['Sprint R&D', 'Validation terrain', 'Scale architecture'],
    icon: Sparkles,
    gradient: 'from-red-500/30 via-red-400/10 to-red-500/20',
    glow: 'bg-red-400/40',
    border: 'border-red-200/80',
    iconTone: 'text-red-700',
    iconShell: 'bg-red-500/15 border-red-200/80',
    progress: 'from-red-500 to-red-500',
    span: 'lg:col-span-2',
  },
] as const;

const INNOVATION_ENGINE_STEPS = [
  {
    phase: '01',
    title: 'Signal Mining',
    detail: 'Audit business, data et parcours utilisateurs pour identifier les opportunites de rupture les plus rentables.',
    outcome: 'Sortie: blueprint priorise et hypotheses mesurables',
  },
  {
    phase: '02',
    title: 'Rapid Prototyping',
    detail: 'Creation d un prototype 3D/IA/AR/VR testable en conditions reelles avec un cadre technique solide.',
    outcome: 'Sortie: preuves terrain, retours usages, priorites produit',
  },
  {
    phase: '03',
    title: 'Scale Delivery',
    detail: 'Industrialisation cloud, monitoring, securite et integration continue pour un lancement sans friction.',
    outcome: 'Sortie: plateforme robuste, equipe autonome, ROI mesurable',
  },
] as const;

export default function Home() {
  const [auditEmail, setAuditEmail] = useState('');
  const [auditMessage, setAuditMessage] = useState<string | null>(null);
  const [isSubmittingAudit, setIsSubmittingAudit] = useState(false);
  const [shouldRenderHero3D, setShouldRenderHero3D] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.innerWidth < 1024) {
      return;
    }

    const schedule = window.requestIdleCallback
      ? window.requestIdleCallback(() => setShouldRenderHero3D(true), { timeout: 1200 })
      : window.setTimeout(() => setShouldRenderHero3D(true), 600);

    return () => {
      if (window.requestIdleCallback && typeof schedule === 'number') {
        window.cancelIdleCallback(schedule);
        return;
      }
      window.clearTimeout(schedule as number);
    };
  }, []);

  const submitAuditRequest = async (event: FormEvent) => {
    event.preventDefault();
    if (!auditEmail.trim()) return;
    setIsSubmittingAudit(true);
    setAuditMessage(null);

    try {
      const response = await apiFetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: 'Prospect Audit',
          email: auditEmail.trim(),
          message: "Demande d'audit depuis la homepage",
        }),
      });
      if (!response.ok) throw new Error('Erreur envoi');
      setAuditEmail('');
      setAuditMessage('Demande envoyee. Nous revenons vers vous sous 24h.');
    } catch {
      setAuditMessage("Impossible d'envoyer la demande pour le moment.");
    } finally {
      setIsSubmittingAudit(false);
    }
  };

  return (
    <div className="relative w-full overflow-x-hidden">
      {shouldRenderHero3D ? (
        <Suspense fallback={<div className="absolute inset-0 -z-10 bg-slate-50" aria-hidden="true" />}>
          <Hero3D />
        </Suspense>
      ) : (
        <div className="absolute inset-0 -z-10 bg-slate-50" aria-hidden="true" />
      )}
      
      {/* Hero Section */}
      <section className="pt-8 pb-20 px-6 container mx-auto relative">
        <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(360px,460px)]">
          <div className="max-w-4xl">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
            <h1 className="text-[clamp(2.6rem,14vw,4.5rem)] md:text-xxl font-extrabold tracking-tight md:tracking-tighter text-slate-900 mb-6 leading-[1.05] [overflow-wrap:anywhere]">
              Des applications web, mobile & SaaS <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-500">
                sans compromis.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-600 mb-10 max-w-2xl font-medium">
              Ahime conçoit des plateformes web, SaaS, apps mobiles et logiciels desktop pour Windows, macOS, Linux et Android. Performants, sécurisés, sur-mesure.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="bg-red-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-red-700 transition-all hover:scale-105 flex items-center gap-2 shadow-xl shadow-red-600/20">
                Démarrer un projet <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/services" className="bg-white text-slate-900 border border-slate-200 px-8 py-4 rounded-full text-lg font-semibold hover:bg-slate-50 transition-all hover:scale-105 shadow-sm">
                Explorer nos expertises
              </Link>
            </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 28, scale: 0.94 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.45 }}
            transition={{ duration: 0.75, ease: 'easeOut' }}
            className="relative hidden lg:block"
          >
            <div className="absolute -left-8 -top-6 h-32 w-32 rounded-full bg-red-400/20 blur-3xl" />
            <div className="absolute -right-6 -bottom-8 h-36 w-36 rounded-full bg-red-400/20 blur-3xl" />
            {shouldRenderHero3D ? (
              <Suspense fallback={<div className="h-[370px] rounded-[2.6rem] border border-white/60 bg-slate-900/90" />}>
                <EcosystemOrb3D />
              </Suspense>
            ) : (
              <div className="h-[370px] rounded-[2.6rem] border border-white/60 bg-slate-900/90" />
            )}
          </motion.div>
        </div>

        {/* Floating Stats (Glassmorphism) */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Projets délivrés", value: "150+" },
            { label: "Performance SEO", value: "+300%" },
            { label: "Uptime Garanti", value: "99.9%" },
            { label: "Satisfaction Client", value: "4.9/5" }
          ].map((stat, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 + (i * 0.1) }}
              className="bg-white/60 backdrop-blur-xl border border-white/40 p-6 rounded-[2.5rem] shadow-sm hover:shadow-md transition-shadow"
            >
              <p className="text-3xl font-extrabold text-slate-900">{stat.value}</p>
              <p className="text-sm font-semibold text-slate-500 mt-1 uppercase tracking-wider">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </section>


      {/* Expertises Section */}
      <section id="expertises" className="bg-slate-50 py-32 px-6">
        <div className="container mx-auto">
        <div className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
          <motion.h2 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-6xl font-black tracking-tight text-slate-900 mb-4"
          >
            Nos Expertises
          </motion.h2>
          <p className="text-xl text-slate-600 max-w-2xl">Une approche produit pour créer des applications web, mobiles, SaaS et desktop prêtes à tourner dans vos opérations.</p>
          </div>
          <Link to="/services" className="inline-flex items-center gap-2 text-red-600 font-black hover:gap-3 transition-all">
            Voir toutes les expertises <ArrowRight className="h-5 w-5" />
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-4">
          {[
            {
              title: 'Applications web & mobiles',
              text: 'Frontends rapides, backends API, apps Android/iOS et intégrations complexes.',
              icon: Code2,
              href: '/services/tech',
              tone: 'text-red-600',
              shell: 'bg-red-50',
              items: ['React / Next / Vite', 'Flutter / React Native', 'APIs & intégrations'],
            },
            {
              title: 'SaaS métier',
              text: 'ERP, CRM, POS, portails clients, abonnements et dashboards.',
              icon: Smartphone,
              href: '/services/btp',
              tone: 'text-red-600',
              shell: 'bg-red-50',
              items: ['Multi-tenant', 'Paiements & factures', 'Backoffice'],
            },
            {
              title: 'Desktop & Linux',
              text: 'Logiciels installables, offline-first, Windows, macOS, Linux et Android.',
              icon: Monitor,
              href: '/services/consulting',
              tone: 'text-red-600',
              shell: 'bg-red-50',
              items: ['Windows / macOS / Linux', 'Offline-first', 'Connecteurs locaux'],
            },
            {
              title: 'APIs, cloud & analytics',
              text: 'Backends robustes, synchronisation, reporting et automatisation en temps réel.',
              icon: BarChart3,
              href: '/services/tech',
              tone: 'text-red-600',
              shell: 'bg-red-50',
              items: ['Cloud & API', 'Reporting', 'Automatisation'],
            },
          ].map((expertise, index) => {
            const Icon = expertise.icon;

            return (
              <motion.article
                key={expertise.title}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: index * 0.08 }}
                className="group flex min-h-[360px] flex-col rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200/70"
              >
                <div className={`mb-6 flex h-12 w-12 items-center justify-center rounded-2xl ${expertise.shell} ${expertise.tone}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="text-2xl font-black tracking-tight text-slate-900">{expertise.title}</h3>
                <p className="mt-3 text-sm font-medium leading-relaxed text-slate-600">{expertise.text}</p>

                <ul className="mt-6 space-y-3">
                  {expertise.items.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-sm font-bold text-slate-700">
                      <CheckCircle2 className={`h-4 w-4 ${expertise.tone}`} />
                      {item}
                    </li>
                  ))}
                </ul>

                <Link to={expertise.href} className={`mt-auto inline-flex items-center gap-2 pt-7 text-sm font-black transition-all group-hover:gap-3 ${expertise.tone}`}>
                  En savoir plus <ArrowRight className="h-4 w-4" />
                </Link>
              </motion.article>
            );
          })}
        </div>
        </div>
      </section>


      {/* Solutions Section */}
      <section id="solutions" className="py-32 bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(220,38,38,0.22),transparent_32%),radial-gradient(circle_at_85%_10%,rgba(220,38,38,0.16),transparent_28%)]" />
        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row justify-between items-end gap-8 mb-14">
            <div className="max-w-3xl">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-4xl md:text-6xl font-black tracking-tight"
              >
                Nos solutions créées par Ahime.
              </motion.h2>
              <p className="mt-5 text-lg md:text-xl text-slate-300 font-medium leading-relaxed">
                Des applications prêtes à servir: travail collaboratif, point de vente, Wi-Fi monétisé et épargne à objectifs.
              </p>
            </div>
            <Link to="/solutions" className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-black text-slate-950 transition-all hover:gap-3 hover:bg-red-50">
              Toutes les solutions <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {SOLUTIONS.map((solution, index) => (
              <motion.article
                key={solution.slug}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="group flex min-h-[360px] flex-col overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.06] p-6 backdrop-blur-xl transition-all hover:-translate-y-1 hover:bg-white/[0.09]"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white">
                    {solution.image ? (
                      <img src={solution.image} alt="" className="max-h-11 max-w-11 object-contain" loading="lazy" decoding="async" />
                    ) : (
                      <span className={`text-xl font-black ${solution.text}`}>{solution.name.slice(0, 2)}</span>
                    )}
                  </div>
                  <span className="rounded-full border border-white/10 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-slate-300">
                    {solution.latestRelease ? solution.latestRelease : 'Web'}
                  </span>
                </div>
                <p className="mt-7 text-xs font-black uppercase tracking-[0.16em] text-white/80">{solution.tagline}</p>
                <h3 className="mt-3 text-3xl font-black text-white">{solution.name}</h3>
                <p className="mt-4 text-sm font-medium leading-relaxed text-slate-300">{solution.summary}</p>

                <div className="mt-auto pt-8 flex flex-wrap gap-3">
                  <Link to={`/solutions/${solution.slug}`} className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-bold text-slate-950 transition-all hover:gap-3">
                    Voir <ArrowRight className="h-4 w-4" />
                  </Link>
                  {solution.downloads.find((download) => download.url) && (
                    <a href={solution.downloads.find((download) => download.url)?.url} className={`inline-flex items-center gap-2 rounded-full bg-gradient-to-r ${solution.accent} px-4 py-2.5 text-sm font-bold text-white`}>
                      Télécharger <Download className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>


      {/* Featured Projects Section */}
      <section id="realisations" className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)]" />
        <div className="container mx-auto px-6 relative z-10">
          <div className="mx-auto flex max-w-6xl flex-col gap-5 md:flex-row md:items-end md:justify-between mb-8">
            <div>
              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-black tracking-tight text-slate-900 mb-3"
              >
                Réalisations <span className="text-red-600">Phares</span>
              </motion.h2>
              <p className="text-base md:text-lg text-slate-600 max-w-2xl">Des produits logiciels livrés avec une vraie exigence d'usage, de performance et de finition.</p>
            </div>
            <Link to="/realisations" className="text-red-600 font-bold flex items-center gap-2 hover:gap-4 transition-all group">
              Voir tout le portfolio <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-5 lg:grid-cols-2">
            {FEATURED_PROJECTS.map((project, i) => (
              <motion.article
                key={project.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.2 }}
                className="group overflow-hidden rounded-[1.6rem] border border-slate-100 bg-white shadow-[0_16px_45px_-36px_rgba(15,23,42,0.55)]"
              >
                <div className="relative aspect-[16/6.5] overflow-hidden bg-slate-100">
                  <img
                    src={project.img}
                    alt={project.title}
                    width={800}
                    height={600}
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    referrerPolicy="no-referrer"
                  />
                  <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-slate-950/28 to-transparent" />
                  <div className="absolute left-5 top-5 flex gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-red-300 shadow-sm" />
                    <span className="h-2.5 w-2.5 rounded-full bg-red-300 shadow-sm" />
                    <span className="h-2.5 w-2.5 rounded-full bg-red-300 shadow-sm" />
                  </div>
                  <span className="absolute right-5 top-5 h-2 w-24 rounded-full bg-white/65 shadow-sm" />
                </div>

                <a href={project.url} target="_blank" rel="noopener noreferrer" className="block p-5">
                  <div className="mb-3 flex flex-wrap items-center gap-3">
                    <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-black uppercase tracking-[0.16em] text-red-700">{project.category}</span>
                    <span className="text-sm font-bold text-slate-400">{project.client}</span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-slate-900 transition-colors group-hover:text-red-600">{project.title}</h3>
                  <p className="mt-2 text-sm font-medium leading-relaxed text-slate-600">{project.description}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {project.tags.map((tag) => (
                      <span key={tag} className="rounded-full border border-slate-200 px-3 py-1 text-xs font-bold text-slate-500">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-black text-red-600 transition-all group-hover:gap-3">
                    Voir le projet <ArrowRight className="h-4 w-4" />
                  </span>
                </a>
              </motion.article>
            ))}
          </div>
        </div>
      </section>


      {/* Testimonials Section */}
      <section id="temoignages" className="py-32 px-6 container mx-auto">
        <div className="text-center mb-20">
          <h2 className="text-4xl font-extrabold text-slate-900 mb-4">Ils nous font confiance</h2>
          <div className="flex justify-center gap-1">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-red-400 text-red-400" />)}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {TESTIMONIALS.map((t, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-sm relative"
            >
              <Quote className="absolute top-8 right-8 w-12 h-12 text-slate-100" />
              <p className="text-xl text-slate-700 mb-8 relative z-10 leading-relaxed italic">"{t.text}"</p>
              <div className="flex items-center gap-4">
                <img
                  src={t.img}
                  alt={t.name}
                  width={100}
                  height={100}
                  loading="lazy"
                  decoding="async"
                  className="w-14 h-14 rounded-2xl object-cover"
                  referrerPolicy="no-referrer"
                />
                <div>
                  <p className="font-bold text-slate-900">{t.name}</p>
                  <p className="text-sm text-slate-500 font-medium">{t.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>


      {/* Innovation Engine Section */}
      <section id="methode" className="relative overflow-hidden bg-slate-950 py-32 px-6 text-white">
        <div className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_18%_20%,rgba(220,38,38,0.23),transparent_45%),radial-gradient(circle_at_82%_70%,rgba(220,38,38,0.2),transparent_38%),linear-gradient(160deg,#020617_0%,#0f172a_48%,#111827_100%)]" />
        <motion.div
          className="absolute -left-28 top-20 -z-10 h-72 w-72 rounded-full bg-red-400/20 blur-[130px]"
          animate={{ scale: [1, 1.25, 1], opacity: [0.35, 0.6, 0.35] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute -right-20 bottom-16 -z-10 h-80 w-80 rounded-full bg-red-400/20 blur-[130px]"
          animate={{ scale: [1.1, 0.92, 1.1], opacity: [0.45, 0.7, 0.45] }}
          transition={{ duration: 9.5, repeat: Infinity, ease: 'easeInOut' }}
        />

        <div className="container mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.75 }}
            className="mb-14 max-w-4xl"
          >
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-white">
              <Zap className="h-4 w-4" />
              Innovation Engine
            </p>
            <h2 className="text-4xl md:text-6xl font-black tracking-tight leading-[1.05]">
              De l'idee a l'impact,
              <span className="block text-white">
                avec une execution en continu.
              </span>
            </h2>
            <p className="mt-6 text-lg md:text-xl text-slate-300 font-medium leading-relaxed max-w-3xl">
              Notre framework combine sprint d'innovation, test en conditions reelles et industrialisation technique pour
              sortir des produits differenciants a un rythme soutenu.
            </p>
          </motion.div>

          <div className="grid gap-10 lg:grid-cols-5 items-start">
            <div className="relative lg:col-span-3 space-y-5">
              <div className="absolute left-[1.35rem] top-7 bottom-7 w-px bg-gradient-to-b from-white/0 via-white/40 to-white/0" />
              {INNOVATION_ENGINE_STEPS.map((step, index) => (
                <motion.article
                  key={step.phase}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.5, delay: index * 0.12 }}
                  className="relative rounded-[2rem] border border-white/12 bg-white/[0.045] p-6 pl-16 backdrop-blur-md"
                >
                  <div className="absolute left-4 top-6 flex h-7 w-7 items-center justify-center rounded-full border border-white/30 bg-white/10 text-[0.62rem] font-black tracking-[0.12em] text-white">
                    {step.phase}
                  </div>
                  <h3 className="text-2xl font-black text-white">{step.title}</h3>
                  <p className="mt-2 text-slate-300 font-medium leading-relaxed">{step.detail}</p>
                  <p className="mt-4 text-xs uppercase tracking-[0.15em] text-white/80 font-black">{step.outcome}</p>
                </motion.article>
              ))}
            </div>

            <motion.aside
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.65, delay: 0.15 }}
              className="lg:col-span-2 rounded-[2.4rem] border border-white/12 bg-white/[0.05] p-8 backdrop-blur-xl shadow-[0_30px_70px_-45px_rgba(220,38,38,0.8)]"
            >
              <p className="text-sm uppercase tracking-[0.2em] text-slate-300 font-black">Cadence moyenne</p>
              <h3 className="mt-2 text-4xl font-black leading-tight">
                1 prototype
                <span className="block text-white">chaque 14 jours</span>
              </h3>
              <p className="mt-4 text-slate-300 font-medium leading-relaxed">
                Nos squads croisent produit, design et engineering pour transformer une intuition en avantage competitif concret.
              </p>

              <div className="mt-8 grid grid-cols-2 gap-3">
                {[
                  { label: 'Sprints R&D', value: '2-6' },
                  { label: 'Taux d adoption', value: '82%' },
                  { label: 'Time-to-value', value: '-45%' },
                  { label: 'NPS equipes', value: '4.8/5' },
                ].map((item) => (
                  <div key={item.label} className="rounded-2xl border border-white/15 bg-white/[0.04] p-4">
                    <p className="text-xl font-black text-white">{item.value}</p>
                    <p className="mt-1 text-[0.7rem] uppercase tracking-[0.14em] font-bold text-slate-300">{item.label}</p>
                  </div>
                ))}
              </div>

              <Link
                to="/contact"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-black uppercase tracking-[0.12em] text-slate-900 transition-all hover:gap-3 hover:bg-red-100"
              >
                Lancer une exploration <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.aside>
          </div>
        </div>
      </section>


      {/* Future Lab Section */}
      <section id="future-lab" className="relative overflow-hidden bg-slate-50 py-32 px-6">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.75 }}
            className="mb-14 max-w-4xl"
          >
            <h2 className="text-4xl md:text-6xl font-black tracking-tight text-slate-900 leading-[1.05]">
              3D, IA, AR, VR et Innovation
              <span className="block text-red-600">
                au même niveau d'ambition.
              </span>
            </h2>
            <p className="mt-6 max-w-3xl text-lg md:text-xl text-slate-600 font-medium leading-relaxed">
              Nous intégrons les technologies avancées quand elles servent un vrai usage: vendre mieux, former plus vite,
              automatiser une opération ou rendre un produit complexe plus simple à comprendre.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            {FUTURE_LAB_TRACKS.map((track, index) => {
              const Icon = track.icon;

              return (
                <motion.article
                  key={track.key}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.55, delay: index * 0.07 }}
                  className={`group rounded-[2rem] border border-slate-100 bg-white p-6 md:p-7 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200/70 ${track.span}`}
                >
                  <div className="flex h-full flex-col">
                    <div className="mb-6 flex items-start justify-between gap-4">
                      <div className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${track.iconShell}`}>
                        <Icon className={`h-6 w-6 ${track.iconTone}`} />
                      </div>
                      <span className="rounded-full bg-slate-50 px-3 py-1 text-xs font-black uppercase tracking-[0.16em] text-slate-500">
                        {track.anchor}
                      </span>
                    </div>

                    <h3 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">{track.title}</h3>
                    <p className="mt-3 text-slate-700/95 font-medium leading-relaxed">{track.description}</p>

                    <div className="mt-6 flex flex-wrap gap-2">
                      {track.chips.map((chip, chipIndex) => (
                        <motion.span
                          key={chip}
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.35, delay: (index * 0.06) + (chipIndex * 0.08) }}
                          className="rounded-full border border-slate-200 px-3 py-1 text-xs font-bold text-slate-600"
                        >
                          {chip}
                        </motion.span>
                      ))}
                    </div>

                    <div className="mt-auto pt-7">
                      <p className="text-[0.67rem] font-extrabold uppercase tracking-[0.2em] text-slate-400">Impact cible</p>
                      <p className="mt-1 text-lg font-black text-slate-900">{track.metric}</p>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>


      {/* Video Section: L'Expérience Ahime */}
      <section id="experience" className="py-32 bg-white relative overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-5xl font-black text-slate-900 tracking-tighter mb-8 leading-tight">
                  L'innovation <br/>
                  <span className="text-red-600">en mouvement.</span>
                </h2>
                <p className="text-xl text-slate-600 mb-8 font-medium leading-relaxed">
                  Découvrez comment nous fusionnons ingénierie de pointe et design d'exception pour créer des plateformes qui redéfinissent les standards de votre industrie.
                </p>
                <ul className="space-y-4 mb-10">
                  {[
                    "Architecture Cloud Native & Scalable",
                    "Interfaces Immersives & Performantes",
                    "Sécurité de Niveau Entreprise",
                    "Intégration IA & Automatisation"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-700 font-bold">
                      <CheckCircle2 className="w-6 h-6 text-red-500" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/a-propos" className="inline-flex items-center gap-2 text-red-600 font-black uppercase tracking-widest text-sm hover:gap-4 transition-all">
                  En savoir plus sur notre méthodologie <ArrowRight className="w-5 h-5" />
                </Link>
              </motion.div>
            </div>
            <div className="lg:w-1/2 relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative aspect-video rounded-[3rem] overflow-hidden shadow-2xl shadow-red-600/10 border-8 border-white group"
              >
                <video 
                  src="https://www.w3schools.com/html/mov_bbb.mp4" 
                  autoPlay 
                  muted 
                  loop 
                  playsInline
                  preload="none"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-slate-900/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                   <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-2xl">
                     <div className="w-0 h-0 border-t-[10px] border-t-transparent border-l-[15px] border-l-red-600 border-b-[10px] border-b-transparent ml-1"></div>
                   </div>
                </div>
              </motion.div>
              {/* Decorative elements */}
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-red-500/10 rounded-full blur-3xl"></div>
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-red-500/10 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>


      {/* Marquee Partenaires */}
      <section id="partenaires" className="py-10 border-y border-slate-200 bg-white overflow-hidden flex" aria-labelledby="partenaires-title">
        <h2 id="partenaires-title" className="sr-only">Nos partenaires technologiques</h2>
        <motion.div 
          animate={{ x: [0, -1035] }}
          transition={{ repeat: Infinity, duration: 25, ease: "linear" }}
          className="flex gap-16 items-center whitespace-nowrap px-8"
        >
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex gap-16 items-center">
              <span className="text-2xl font-bold text-slate-300">Vercel</span>
              <span className="text-2xl font-bold text-slate-300">AWS</span>
              <span className="text-2xl font-bold text-slate-300">Laravel</span>
              <span className="text-2xl font-bold text-slate-300">Next.js</span>
              <span className="text-2xl font-bold text-slate-300">Stripe</span>
              <span className="text-2xl font-bold text-slate-300">Tailwind</span>
              <span className="text-2xl font-bold text-slate-300">Shopify</span>
              <span className="text-2xl font-bold text-slate-300">WordPress</span>
              <span className="text-2xl font-bold text-slate-300">Odoo</span>
              <span className="text-2xl font-bold text-slate-300">Wix</span>
              <span className="text-2xl font-bold text-slate-300">Prestashop</span>
              <span className="text-2xl font-bold text-slate-300">HubSpot</span>
              <span className="text-2xl font-bold text-slate-300">Contentful</span>
              <span className="text-2xl font-bold text-slate-300">Algolia</span>
              <span className="text-2xl font-bold text-slate-300">Firebase</span>
              <span className="text-2xl font-bold text-slate-300">Netlify</span>
              <span className="text-2xl font-bold text-slate-300">Superbase</span>
              <span className="text-2xl font-bold text-slate-300">Strapi</span>
              <span className="text-2xl font-bold text-slate-300">Sanity</span>
              <span className="text-2xl font-bold text-slate-300">Buble</span>
              <span className="text-2xl font-bold text-slate-300">Svelte</span>
              <span className="text-2xl font-bold text-slate-300">React</span>
              <span className="text-2xl font-bold text-slate-300">Vue</span>
              <span className="text-2xl font-bold text-slate-300">Gatsby</span>
              <span className="text-2xl font-bold text-slate-300">GraphQL</span>
              <span className="text-2xl font-bold text-slate-300">Apollo</span>
              <span className="text-2xl font-bold text-slate-300">Prisma</span>
              <span className="text-2xl font-bold text-slate-300">MongoDB</span>
              <span className="text-2xl font-bold text-slate-300">MySQL</span>
              <span className="text-2xl font-bold text-slate-300">PostgreSQL</span>
              <span className="text-2xl font-bold text-slate-300">Redis</span>
              <span className="text-2xl font-bold text-slate-300">Elasticsearch</span>
              <span className="text-2xl font-bold text-slate-300">Elastic</span>  
              <span className="text-2xl font-bold text-slate-300">Kubernetes</span>
              <span className="text-2xl font-bold text-slate-300">Docker</span>
            </div>
          ))}
        </motion.div>
      </section>


      {/* Lead Gen Footer Form */}
      <section className="py-24 px-6 bg-slate-900 text-white rounded-t-[4rem] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-red-500 rounded-full blur-[120px]"></div>
        </div>
        
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl font-extrabold mb-6">Prêt à accélérer ?</h2>
            <p className="text-xl text-slate-400 mb-12 max-w-2xl mx-auto">
              Laissez-nous vos coordonnées, nos experts vous recontactent sous 24h pour un audit gratuit de votre écosystème.
            </p>
            <form className="flex flex-col md:flex-row gap-4 max-w-2xl mx-auto" onSubmit={submitAuditRequest}>
              <input 
                type="email" 
                value={auditEmail}
                onChange={(e) => setAuditEmail(e.target.value)}
                placeholder="votre@email.com" 
                required
                className="flex-1 px-8 py-5 rounded-2xl bg-slate-800 border border-slate-700 text-white focus:outline-none focus:ring-4 focus:ring-red-500/30 text-lg"
              />
              <button type="submit" disabled={isSubmittingAudit} className="bg-red-600 text-white px-10 py-5 rounded-2xl text-lg font-bold hover:bg-red-500 transition-all shadow-lg shadow-red-600/20 disabled:opacity-70">
                {isSubmittingAudit ? 'Envoi...' : 'Demander un audit'}
              </button>
            </form>
            {auditMessage && <p className="mt-4 text-sm text-slate-300">{auditMessage}</p>}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
